<script setup lang="ts">
import { ref, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';
import type { ServiceItem } from './OurServices.vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const props = defineProps<{
  post: {
    title?: string | undefined;
    featured_image_url?: string;
    fields?: {
      label?: string;
      show_decor?: boolean;
      single_featured_image?: { url?: string };
      sections?: { acf_fc_layout: string }[];
    };
    content?: string;
  };
  services?: ServiceItem[];
}>();

const frontUrl = ref('');

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger);

  if (typeof window !== 'undefined') {
    frontUrl.value = window.location.href;
  }
});

const heroRef = ref<HTMLElement | null>(null);
const heroBgRef = ref<HTMLElement | null>(null);

onMounted(() => {
  if (heroRef.value) {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.value,
        start: 'top top',
        end: 'bottom+=600px bottom',
        scrub: 1,
      },
    });

    if (heroBgRef.value) {
      tl.to(heroBgRef.value, { yPercent: -100 });
    }
  }
});
</script>

<template>
  <section
    ref="heroRef"
    class="relative z-10 bg-cyan-500 pb-12 pt-22.5 md:pt-28"
  >
    <span
      ref="heroBgRef"
      class="absolute inset-0 -z-20 bg-gradient-to-b from-beige"
    ></span>
    <div class="container pt-5 lg:pt-10">
      <div class="mx-auto max-w-4xl items-center gap-10">
        <svg
          class="relative z-10 mb-9 block h-auto w-full origin-center object-cover transition-all duration-500 group-hover:scale-105"
          width="901"
          height="443"
          fill="none"
          viewBox="0 0 901 443"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <defs>
            <clipPath
              :id="`clip-path-img`"
              :key="`clip-img`"
              class="origin-center transition-all duration-500 group-hover:scale-95"
            >
              <path
                d="M21.5796 11.322C60.4947 -13.172 433.938 11.3217 519.912 11.322C632 11.3224 829.119 -14.1523 880.153 11.3214C913.598 28.0153 893.446 87.5892 893.446 204.747C893.446 321.905 922.675 415.961 866.145 433.597C830.409 444.745 619.315 447.155 479 433.597C385 424.514 347.133 433.597 281.754 433.597C216.374 433.597 105.567 455.24 45.5956 424.514C-14.3755 393.788 8.26183 291.532 8.26246 204.747C8.26309 117.962 -17.3355 35.8159 21.5796 11.322Z"
              />
            </clipPath>
          </defs>
          <image
            x="0"
            y="0"
            width="901"
            height="443"
            class="h-full w-full object-cover"
            preserveAspectRatio="xMidYMid slice"
            :xlink:href="
              props.post?.featured_image_url
                ? props.post?.featured_image_url
                : props.post?.fields?.single_featured_image?.url
            "
            :clip-path="`url(#clip-path-img)`"
          />
        </svg>
        <div>
          <h1 v-if="props.post.title" class="h5 mb-4 font-mono md:text-[60px]">
            {{ props.post.title }}
          </h1>
          <div class="flex flex-wrap items-center justify-between gap-4">
            <p
              v-if="post.fields?.label"
              class="block font-mono text-md uppercase tracking-wide"
              :datetime="post.fields?.label"
            >
              {{ post.fields?.label }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
  <section
    class="before:bg-position-right relative z-10 bg-white bg-left-top bg-repeat-y pb-16 pt-15 before:absolute before:inset-0 before:-z-10 before:bg-right before:bg-repeat-y before:content-[''] md:py-15"
    :class="
      props.post.fields?.show_decor &&
      `md:bg-[url('/img/post-decor-l.svg')] md:before:bg-[url('/img/post-decor-r.svg')]`
    "
  >
    <div class="container lg:py-4">
      <div class="mx-auto max-w-4xl">
        <div
          v-if="props.post.content"
          class="typography"
          v-html="
            sanitizeHtml(props.post.content, {
              allowedAttributes: false,
              allowedTags: false,
              allowVulnerableTags: true,
              transformTags: {
                script: () => ({
                  tagName: 'noscript',
                  attribs: {},
                }),
              },
            })
          "
        />
      </div>
    </div>
  </section>
  <OurServices v-if="services" :section="{ services: services }" />
</template>
